import React, { useRef, useMemo, useCallback } from 'react';

const BRAND_COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  '#FF9F40', '#C9CBCF', '#7BC8A4', '#E7E9ED', '#FF6384'
];

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VideoFrameTimeline = ({ frames, currentFrameIndex, onFrameChange, fps = 30, filterSlot }) => {
  const timelineRef = useRef(null);

  // Build brand → color mapping
  const brandColorMap = useMemo(() => {
    const allBrands = new Set();
    frames.forEach(f => (f.brands || []).forEach(b => allBrands.add(b)));
    const map = {};
    [...allBrands].forEach((b, i) => { map[b] = BRAND_COLORS[i % BRAND_COLORS.length]; });
    return map;
  }, [frames]);

  // Compute timeline range
  const maxFrame = useMemo(() => {
    if (frames.length === 0) return 1;
    return Math.max(...frames.map(f => f.frame_number));
  }, [frames]);

  const totalDuration = maxFrame / fps;
  const currentFrame = frames[currentFrameIndex] || frames[0];

  const handleTimelineClick = useCallback((e) => {
    if (!timelineRef.current || frames.length === 0) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const fraction = clickX / rect.width;
    const targetFrame = Math.round(fraction * maxFrame);

    // Find nearest detection frame
    let nearestIdx = 0;
    let nearestDist = Infinity;
    frames.forEach((f, i) => {
      const dist = Math.abs(f.frame_number - targetFrame);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestIdx = i;
      }
    });
    onFrameChange(nearestIdx);
  }, [frames, maxFrame, onFrameChange]);

  if (frames.length === 0) return null;

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Navigation controls */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onFrameChange(0)}
            disabled={currentFrameIndex === 0}
            title="First frame"
          >
            ◀◀
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onFrameChange(Math.max(0, currentFrameIndex - 1))}
            disabled={currentFrameIndex === 0}
            title="Previous frame"
          >
            ◀
          </button>
          <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem', minWidth: '180px', textAlign: 'center' }}>
            Frame {currentFrameIndex + 1} of {frames.length}
            {currentFrame && (
              <span style={{ color: 'var(--text-secondary)', marginLeft: '8px' }}>
                {formatTime(currentFrame.time_seconds)} / {formatTime(totalDuration)}
              </span>
            )}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onFrameChange(Math.min(frames.length - 1, currentFrameIndex + 1))}
            disabled={currentFrameIndex >= frames.length - 1}
            title="Next frame"
          >
            ▶
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onFrameChange(frames.length - 1)}
            disabled={currentFrameIndex >= frames.length - 1}
            title="Last frame"
          >
            ▶▶
          </button>
        </div>
        <div className="d-flex align-items-center gap-3">
          {filterSlot}
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            {currentFrame?.detection_count || 0} detections
            {currentFrame?.brands && currentFrame.brands.length > 0 && (
              <span> · {currentFrame.brands.join(', ')}</span>
            )}
          </span>
        </div>
      </div>

      {/* Timeline bar */}
      <div
        ref={timelineRef}
        onClick={handleTimelineClick}
        style={{
          position: 'relative',
          height: '32px',
          backgroundColor: 'var(--background-secondary)',
          borderRadius: '4px',
          border: '1px solid var(--border-color)',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        {/* Detection frame markers */}
        {frames.map((frame, idx) => {
          const position = (frame.frame_number / maxFrame) * 100;
          const primaryBrand = frame.brands?.[0];
          const color = primaryBrand ? (brandColorMap[primaryBrand] || '#6c757d') : '#6c757d';
          const isCurrent = idx === currentFrameIndex;

          return (
            <div
              key={frame.frame_number}
              onClick={(e) => { e.stopPropagation(); onFrameChange(idx); }}
              style={{
                position: 'absolute',
                left: `${position}%`,
                top: '2px',
                bottom: '2px',
                width: isCurrent ? '4px' : '2px',
                backgroundColor: color,
                opacity: isCurrent ? 1 : 0.5,
                borderRadius: '1px',
                transform: 'translateX(-50%)',
                transition: 'opacity 0.1s',
                zIndex: isCurrent ? 2 : 1,
              }}
              title={`Frame ${frame.frame_number} (${formatTime(frame.time_seconds)}) - ${frame.detection_count} detections`}
            />
          );
        })}

        {/* Current position indicator */}
        {currentFrame && (
          <div style={{
            position: 'absolute',
            left: `${(currentFrame.frame_number / maxFrame) * 100}%`,
            top: '-2px',
            bottom: '-2px',
            width: '3px',
            backgroundColor: 'var(--accent-color)',
            borderRadius: '2px',
            transform: 'translateX(-50%)',
            zIndex: 3,
            boxShadow: '0 0 4px var(--accent-color)',
          }} />
        )}
      </div>

      {/* Brand color legend */}
      {Object.keys(brandColorMap).length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-1">
          {Object.entries(brandColorMap).map(([brand, color]) => (
            <span key={brand} style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
              <span style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                backgroundColor: color,
                borderRadius: '50%',
                marginRight: '3px',
              }} />
              {brand}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoFrameTimeline;
